import { parse } from 'query-string'
import {
  HIDE_CHROME_STORAGE_KEY,
  FOOTER_CONTENT_HEIGHT,
  HEADER_CONTENT_HEIGHT,
  MENU_PANEL_WIDTH_EXPANDED,
  MENU_PANEL_WIDTH_COLLAPSED,
  MENU_EXPANDED_STORAGE_KEY
} from './constants'

const MENU_WIDTH_TRANSITION_MSEC = 300

// Overridable: the constants size the hub chrome, which a public app does not mount.
let headerContentHeight: string = HEADER_CONTENT_HEIGHT
let footerContentHeight: string = FOOTER_CONTENT_HEIGHT

function dispatchMenuLayoutChangeEvents(): void {
  window.dispatchEvent(new CustomEvent('menuExpandedChanged'))
  window.dispatchEvent(new Event('resize'))
  window.setTimeout(() => {
    window.dispatchEvent(new Event('resize'))
  }, MENU_WIDTH_TRANSITION_MSEC)
}

/**
 * Function which obtains whether we should hide chrome due to query parameter or session setting
 * @param {String | undefined} - the query parameter string to substitute instead of the one from global location
 * @return {Boolean} - whether we should be in hide chrome mode
 */
export function getHideChromeMode(search?: string): boolean {
  const hideChromeQP = parse(search || location.search).hideChrome == 'true'
  if (hideChromeQP) {
    sessionStorage.setItem(HIDE_CHROME_STORAGE_KEY, 'true')
  }
  return (
    hideChromeQP || sessionStorage.getItem(HIDE_CHROME_STORAGE_KEY) === 'true'
  )
}

/**
 * Overrides the heights reported below, for apps that mount their own chrome
 * @param {String | undefined} header - header height, in the form of "60px"
 * @param {String | undefined} footer - footer height, in the form of "80px"
 */
export function setChromeContentHeights(
  header?: string,
  footer?: string
): void {
  headerContentHeight = header || HEADER_CONTENT_HEIGHT
  footerContentHeight = footer || FOOTER_CONTENT_HEIGHT
}

/**
 * Returns the height occupied by the chrome header
 * @return {String} - a string in the form of "60px"
 */
export function getHeaderContentHeight(hasHeader?: boolean): string {
  return getHideChromeMode() ||
    (hasHeader !== true && !document.querySelector('#appHeader'))
    ? '0px'
    : headerContentHeight
}

/**
 * Returns the height occupied by the chrome footer
 * @return {String} - a string in the form of "60px"
 */
export function getFooterContentHeight(hasFooter?: boolean): string {
  // @ts-ignore
  return getHideChromeMode() ||
    (hasFooter !== true && !document.querySelector('#appFooter'))
    ? '0px'
    : footerContentHeight
}

/**
 * Function which obtains whether the menu should be expanded based on localStorage setting
 * @return {Boolean} - whether the menu should be in expanded mode (defaults to true)
 */
export function getMenuExpandedMode(): boolean {
  const stored = localStorage.getItem(MENU_EXPANDED_STORAGE_KEY)
  // Default to true (expanded) if no value is stored
  return stored === null ? true : stored === 'true'
}

/**
 * Function which sets the menu expanded mode in localStorage and notifies other components
 * @param {Boolean} isExpanded - whether the menu should be expanded
 * @return {Boolean} - the value that was set (for convenience)
 */
export function setMenuExpandedMode(isExpanded: boolean): boolean {
  localStorage.setItem(MENU_EXPANDED_STORAGE_KEY, isExpanded.toString())
  // Dispatch custom event to notify other components immediately (same-tab changes)
  dispatchMenuLayoutChangeEvents()
  return isExpanded
}

/**
 * Function which toggles the menu expanded mode and returns the new state
 * @return {Boolean} - the new expanded state after toggling
 */
export function toggleMenuExpandedMode(): boolean {
  const currentState = getMenuExpandedMode()
  return setMenuExpandedMode(!currentState)
}

/**
 * Returns the width occupied by the chrome menu panel
 * @return {String} - a string in the form of "60px"
 */
export function getMenuPanelWidth(): string {
  if (getHideChromeMode()) {
    return '0px'
  }
  return getMenuExpandedMode()
    ? MENU_PANEL_WIDTH_EXPANDED
    : MENU_PANEL_WIDTH_COLLAPSED
}
